import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import ConfirmActionModal from '../../components/modals/ConfirmActionModal';
import '../../styles/Table.css';

const SitesListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    fetchSites();
  }, [searchTerm]);

  // Debounced search handler using ref (similar to People page)
  const handleSearchChange = (e) => {
    const value = e.target.value;

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 500);
  };

  const fetchSites = async () => {
    try {
      // Avoid toggling loading true on every search to prevent flicker
      const params = {};
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const res = await api.getSites(params);
      if (res.success) {
        const activeElement = document.activeElement;
        const wasSearchFocused = activeElement === searchInputRef.current;

        setSites(res.data || []);

        if (wasSearchFocused && searchInputRef.current) {
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
      } else {
        setSites([]);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast.error('Error fetching sites');
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteSite(id);
      toast.success('Site deleted successfully');
      fetchSites();
    } catch (error) {
      console.error('Error deleting site:', error);
      toast.error('Error deleting site');
    }
  };

  if (loading && sites.length === 0) {
    return <Loader />;
  }

  return (
    <div className="sites-list-page" style={{ padding: '16px' }}>
      {/* Actions */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div style={{ flex: 1, marginRight: '12px' }}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search sites by name..."
            defaultValue={searchTerm}
            onChange={handleSearchChange}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
            }}
          />
        </div>
        <button
          onClick={() => navigate('/admin/sites/add')}
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>+</span>
          <span>Add Site</span>
        </button>
      </div>

      {/* Table */}
      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {sites.length === 0 && !loading ? (
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              color: '#718096',
              fontSize: '14px',
            }}
          >
            No sites found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                    }}
                  >
                    Site
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                    }}
                  >
                    Location
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                    }}
                  >
                    Contact
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                      width: '120px',
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => {
                  const id = site._id || site.id;
                  const address = site.address || {};
                  const contact = site.contactPerson || {};
                  const locationParts = [
                    address.city,
                    address.state,
                    address.country,
                  ].filter(Boolean);
                  const locationText =
                    locationParts.join(', ') || address.street || 'N/A';

                  return (
                    <tr key={id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px', color: '#2d3748', fontWeight: 600, fontSize: '13px' }}>
                        {site.name}
                      </td>
                      <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px' }}>
                        {locationText}
                      </td>
                      <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px' }}>
                        {contact.name || contact.email || contact.phone
                          ? `${contact.name || ''}${
                              contact.email ? ` (${contact.email})` : ''
                            }${contact.phone ? ` - ${contact.phone}` : ''}`
                          : 'N/A'}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            onClick={() => navigate(`/admin/sites/${id}/edit`)}
                            style={{
                              padding: '6px 10px',
                              background: '#e6fffa',
                              color: '#234e52',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 500,
                            }}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => setDeleteTarget(id)}
                            style={{
                              padding: '6px 10px',
                              background: '#fed7d7',
                              color: '#c53030',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 500,
                            }}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirm modal */}
      <ConfirmActionModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            handleDelete(deleteTarget);
          }
        }}
        title="Delete Site"
        message="Are you sure you want to delete this site? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default SitesListPage;

